import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import type { MarketReportKeyFinding } from '@horwath-htl/shared-types';

const FindingNumberChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  minWidth: 32,
  height: 32,
  '& .MuiChip-label': {
    padding: '0 8px'
  }
}));

export interface KeyFindingCardProps {
  finding: MarketReportKeyFinding;
  defaultExpanded?: boolean;
  onExpand?: (expanded: boolean) => void;
  elevation?: number;
}

export const KeyFindingCard: React.FC<KeyFindingCardProps> = ({
  finding,
  defaultExpanded = false,
  onExpand,
  elevation = 1
}) => {
  // Always show full content - no expansion state needed
  return (
    <Card elevation={elevation} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <FindingNumberChip label={finding.findingNumber} size="small" />
          <Typography variant="h6" component="h3" sx={{ flex: 1, lineHeight: 1.3 }}>
            {finding.findingTitle}
          </Typography>
          {getIconForFinding(finding.findingTitle)}
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {finding.findingDescription}
        </Typography>
      </CardContent>
    </Card>
  );
};

function getIconForFinding(title: string): React.ReactNode {
  const lowerTitle = title.toLowerCase();
  
  if (lowerTitle.includes('growth') || lowerTitle.includes('increase') || lowerTitle.includes('up')) {
    return (
      <Tooltip title="Positive trend">
        <TrendingUpIcon color="success" />
      </Tooltip>
    );
  }
  
  if (lowerTitle.includes('decline') || lowerTitle.includes('decrease') || lowerTitle.includes('down')) {
    return (
      <Tooltip title="Negative trend">
        <TrendingUpIcon color="error" sx={{ transform: 'rotate(180deg)' }} />
      </Tooltip>
    );
  }
  
  return (
    <Tooltip title="Key insight">
      <InfoIcon color="primary" />
    </Tooltip>
  );
}